
import React, { useState } from 'react';
import { Layers, CheckCircle2, AlertCircle, RefreshCw, Lock, ArrowRight, X, Loader2, Database, Wifi, FileText, Calendar, ShieldCheck, Search, Key, Scale } from 'lucide-react';

type IntegrationType = 'BMD' | 'RZL' | 'FINANZONLINE' | null;

const IntegrationsHub: React.FC = () => {
  const [activeModal, setActiveModal] = useState<IntegrationType>(null);
  const [simulationStep, setSimulationStep] = useState(0);

  // Reset state when closing modal
  const closeModal = () => {
    setActiveModal(null);
    setSimulationStep(0);
  };

  // Simulate Connection for BMD
  const runConnectionTest = () => {
      setSimulationStep(1); // Connecting
      setTimeout(() => setSimulationStep(2), 800); // Handshake
      setTimeout(() => setSimulationStep(3), 2000); // Success
  };

  // Simulate FinanzOnline OCR
  const runFinanzOnlineCheck = () => {
      setSimulationStep(1); // Login
      setTimeout(() => setSimulationStep(2), 1500); // Found Doc
      setTimeout(() => setSimulationStep(3), 3500); // OCR Scanning
      setTimeout(() => setSimulationStep(4), 5500); // Calendar Entry
  };

  const renderModalContent = () => {
      switch (activeModal) {
        case 'BMD':
            return (
                <div className="space-y-6">
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-slate-900 font-black text-xl">BMD</div>
                        <div>
                            <h3 className="text-xl font-bold text-white">BMD NTCS Export</h3>
                            <p className="text-slate-400 text-sm">Kompatibles SOAP-Format</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-xs text-slate-500 uppercase font-bold">SOAP Endpoint URL</label>
                            <input type="text" value="https://ws.bmd-cloud.at/ntcs/v2/soap" readOnly className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 font-mono text-sm focus:border-blue-500 outline-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <div>
                                <label className="text-xs text-slate-500 uppercase font-bold">Mandant ID</label>
                                <input type="text" value="89210" readOnly className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 font-mono text-sm" />
                             </div>
                             <div>
                                <label className="text-xs text-slate-500 uppercase font-bold">Service User</label>
                                <input type="text" value="orbit_svc_01" readOnly className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 font-mono text-sm" />
                             </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-700">
                        {simulationStep === 0 && (
                            <button onClick={runConnectionTest} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-all flex items-center justify-center">
                                <Wifi className="w-4 h-4 mr-2" />
                                Verbindung testen
                            </button>
                        )}
                        {simulationStep === 1 && (
                             <button disabled className="w-full bg-slate-700 text-slate-300 font-bold py-3 rounded-lg flex items-center justify-center cursor-wait">
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Handshake Init...
                            </button>
                        )}
                        {simulationStep >= 2 && simulationStep < 3 && (
                             <button disabled className="w-full bg-slate-700 text-slate-300 font-bold py-3 rounded-lg flex items-center justify-center cursor-wait">
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Verifying Certificates...
                            </button>
                        )}
                        {simulationStep === 3 && (
                             <button className="w-full bg-emerald-600/20 border border-emerald-500/50 text-emerald-400 font-bold py-3 rounded-lg flex items-center justify-center">
                                <CheckCircle2 className="w-5 h-5 mr-2" />
                                Connected (Ping: 24ms)
                            </button>
                        )}
                    </div>
                </div>
            );
        
        case 'FINANZONLINE':
            return (
                <div className="space-y-6">
                    <div className="flex items-center space-x-4 mb-6">
                         <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center text-white font-bold text-xs text-center border border-slate-700">Finanz<br/>Online</div>
                         <div>
                            <h3 className="text-xl font-bold text-white">Fristen-Wächter AI</h3>
                            <p className="text-slate-400 text-sm">Offizielle Webservice Schnittstelle</p>
                        </div>
                    </div>

                    {simulationStep === 0 && (
                        <div className="space-y-6">
                            {/* Compliance Info Box */}
                            <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg flex items-start">
                                <Scale className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <strong className="text-blue-300 block mb-1">Rechtlicher Hinweis (Österreich)</strong>
                                    <span className="text-slate-300">
                                        Wir nutzen den offiziellen <strong>Webservice-User</strong> (Teilnehmer-ID). 
                                        Es erfolgt <span className="text-red-300">kein</span> Login via ID Austria oder Handy-Signatur. 
                                        Die Datennutzung erfolgt im Rahmen des Auftragsverarbeitungsvertrages (AVV).
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-slate-500 uppercase font-bold">Teilnehmer-ID</label>
                                    <input type="text" value="102839210" readOnly className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 font-mono text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500 uppercase font-bold">Benutzer-ID (API)</label>
                                    <input type="text" value="WS_ORBIT_01" readOnly className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 font-mono text-sm" />
                                </div>
                                <div className="col-span-2">
                                     <label className="text-xs text-slate-500 uppercase font-bold">PIN / Token</label>
                                     <div className="flex items-center bg-slate-950 border border-slate-700 rounded-lg px-4 py-3">
                                        <Key className="w-4 h-4 text-slate-500 mr-2" />
                                        <input type="password" value="****************" readOnly className="bg-transparent w-full text-slate-300 font-mono text-sm outline-none" />
                                     </div>
                                </div>
                            </div>

                             <button 
                                 onClick={runFinanzOnlineCheck}
                                 className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg shadow-lg flex items-center justify-center transition-all"
                             >
                                 <Search className="w-4 h-4 mr-2" />
                                 DataBox Abruf starten
                             </button>
                        </div>
                    )}

                    {simulationStep === 1 && (
                         <div className="bg-slate-950 border border-slate-700 h-64 rounded-xl flex flex-col items-center justify-center">
                             <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                             <p className="text-white font-medium">Authentifiziere Webservice...</p>
                             <p className="text-slate-500 text-sm">SSL Handshake: finanzonline.bmf.gv.at</p>
                         </div>
                    )}

                    {simulationStep >= 2 && simulationStep <= 3 && (
                         <div className="space-y-4">
                             <div className="bg-slate-900 border border-slate-700 p-4 rounded-lg flex items-start animate-fade-in-up">
                                 <div className="p-3 bg-red-500/20 rounded mr-4">
                                     <FileText className="w-6 h-6 text-red-400" />
                                 </div>
                                 <div className="flex-1">
                                     <h4 className="text-white font-medium">Einkommensteuerbescheid 2024.pdf</h4>
                                     <p className="text-xs text-slate-500 mb-2">Zustellung: Heute, 08:30</p>
                                     
                                     {simulationStep === 3 && (
                                         <div className="bg-slate-950 p-2 rounded border border-slate-800 text-xs font-mono text-emerald-400">
                                             <div className="flex items-center mb-1">
                                                 <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                                 AI OCR Scanning...
                                             </div>
                                             <div className="opacity-70">
                                                 Found: "Beschwerdefrist" <br/>
                                                 Found: "innerhalb eines Monats" <br/>
                                                 Calculating Deadline...
                                             </div>
                                         </div>
                                     )}
                                 </div>
                             </div>
                         </div>
                    )}

                    {simulationStep === 4 && (
                         <div className="bg-emerald-900/10 border border-emerald-500/30 p-6 rounded-xl text-center animate-fade-in-up">
                             <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                                 <CheckCircle2 className="w-8 h-8 text-white" />
                             </div>
                             <h4 className="text-xl font-bold text-white">Frist eingetragen</h4>
                             <p className="text-emerald-400 text-sm mt-2 font-medium">Deadline: 20.03.2026</p>
                             
                             <div className="mt-6 bg-slate-900 rounded-lg p-3 flex items-center text-left border border-slate-800">
                                 <Calendar className="w-10 h-10 text-blue-400 mr-3" />
                                 <div>
                                     <div className="text-white text-xs font-bold">Outlook Kalender Sync</div>
                                     <div className="text-slate-400 text-[10px]">Fristablauf: ESt-Bescheid 2024 (Mandant Müller)</div>
                                 </div>
                                 <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto" />
                             </div>
                             
                             <p className="text-xs text-slate-500 mt-4 italic">
                                 Hinweis: Bitte Originalbescheid prüfen. Automatischer Eintrag gemäß AVV.
                             </p>
                         </div>
                    )}
                </div>
            );
        
        case 'RZL':
            return (
                <div className="space-y-6">
                    <div className="flex items-center space-x-4 mb-6">
                         <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-slate-900 font-black text-xl tracking-tighter">RZL</div>
                         <div>
                            <h3 className="text-xl font-bold text-white">RZL Board Export</h3>
                            <p className="text-slate-400 text-sm">Local File System Bridge</p>
                        </div>
                    </div>
                    
                    <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-lg flex items-start">
                        <AlertCircle className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-slate-300">
                            <strong>On-Premise Gateway benötigt.</strong><br/>
                            Da RZL oft lokal läuft, muss der "Orbit Local Agent" auf dem Kanzlei-Server installiert sein.
                        </div>
                    </div>

                    <div>
                         <label className="text-xs text-slate-500 uppercase font-bold mb-2 block">Überwachter Ordner (Hotfolder)</label>
                         <div className="flex">
                             <input type="text" value="C:\RZL\Board\Import\Orbit_Inbound" readOnly className="flex-1 bg-slate-950 border border-slate-700 rounded-l-lg px-4 py-3 text-slate-300 font-mono text-sm focus:border-blue-500 outline-none" />
                             <button className="bg-slate-800 border border-slate-700 border-l-0 rounded-r-lg px-4 hover:bg-slate-700 text-slate-400">
                                 <Database className="w-4 h-4" />
                             </button>
                         </div>
                    </div>
                    
                    <button className="w-full bg-slate-800 hover:bg-slate-700 text-white font-medium py-3 rounded-lg border border-slate-700 transition-all">
                        Agent Downloaden (v1.4.exe)
                    </button>
                </div>
            );

        default:
            return null;
      }
  };

  return (
    <div className="animate-fade-in space-y-8 relative">
      
      {/* --- MODAL OVERLAY --- */}
      {activeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md transition-opacity duration-300" onClick={closeModal}></div>
              <div className="bg-slate-900 border border-white/10 p-8 rounded-2xl shadow-2xl w-full max-w-lg relative transform transition-all animate-fade-in-up scale-100 ring-1 ring-white/5">
                  <button onClick={closeModal} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
                      <X className="w-5 h-5" />
                  </button>
                  {renderModalContent()}
              </div>
          </div>
      )}

      <header className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">Orbit Connect</h2>
          <p className="text-slate-400">Das "Österreich-Ökosystem". Deep Integration Layer.</p>
        </div>
        <div className="glass-panel px-4 py-2 rounded-lg text-sm font-medium flex items-center text-blue-300 border border-blue-500/30 shadow-[0_0_15px_-3px_rgba(59,130,246,0.3)]">
             <Layers className="w-4 h-4 mr-2" />
             USP Modul
        </div>
      </header>

      {/* Hero Box - Apple Style */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-950 border border-white/5 p-8 rounded-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] -mr-20 -mt-20 group-hover:bg-blue-600/20 transition-all duration-1000"></div>
          
          <div className="relative z-10 max-w-2xl">
              <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">Kanzlei-Software ist komplex. Wir machen sie einfach.</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">
                  Amerikanische KI-Modelle kennen keine österreichischen Kollektivverträge und keine RZL-Klientenverwaltung. 
                  <strong>Orbit Connect</strong> ist die Brücke zwischen der Intelligenz (Gemini) und Ihrer Kanzlei-Software.
              </p>
              <div className="flex items-center space-x-4">
                  <div className="flex items-center text-xs text-emerald-400 bg-emerald-950/30 px-3 py-1.5 rounded-full border border-emerald-900/50 shadow-sm backdrop-blur-sm">
                      <Lock className="w-3 h-3 mr-2" />
                      DSGVO Filter Active
                  </div>
                  <div className="flex items-center text-xs text-blue-400 bg-blue-950/30 px-3 py-1.5 rounded-full border border-blue-900/50 shadow-sm backdrop-blur-sm">
                      <RefreshCw className="w-3 h-3 mr-2" />
                      Live Sync (5min)
                  </div>
              </div>
          </div>
      </div>

      <div className="flex items-center mt-12 mb-6">
          <div className="w-1 h-6 bg-blue-500 rounded-full mr-3 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
          <h3 className="text-xl font-bold text-white">Kanzlei Integrationen</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* BMD */}
          <div 
            onClick={() => setActiveModal('BMD')}
            className="bg-slate-900/50 backdrop-blur-sm border border-white/5 p-6 rounded-2xl relative group hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer hover:shadow-2xl hover:shadow-blue-900/20"
          >
              <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-slate-900 font-black text-xl shadow-lg">BMD</div>
                  <div className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold px-2 py-1 rounded-full flex items-center">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-pulse"></div> ACTIVE
                  </div>
              </div>
              <h4 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">BMD NTCS Export</h4>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                  Erstellung von Import-Dateien für die CRM-Datenbank. Zuordnung über Klienten-Nummer.
              </p>
              <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-center">
                  <span className="text-xs text-slate-500 font-mono">API: v2.1 (SOAP)</span>
                  <span className="text-blue-400 text-xs font-bold flex items-center opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0 duration-300">
                      Config <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
              </div>
          </div>

          {/* RZL */}
          <div 
            onClick={() => setActiveModal('RZL')}
            className="bg-slate-900/50 backdrop-blur-sm border border-white/5 p-6 rounded-2xl relative group hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer hover:shadow-2xl hover:shadow-blue-900/20"
          >
              <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-slate-900 font-black text-xl tracking-tighter shadow-lg">RZL</div>
                  <div className="bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 text-[10px] font-bold px-2 py-1 rounded-full flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" /> BETA
                  </div>
              </div>
              <h4 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">RZL Board Export</h4>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                  Vorbereitung zur Ablage von PDF-Protokollen im Dokumenten-Management-System (DMS).
              </p>
              <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-center">
                  <span className="text-xs text-slate-500 font-mono">File Connector</span>
                  <span className="text-blue-400 text-xs font-bold flex items-center opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0 duration-300">
                      Setup <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
              </div>
          </div>

          {/* FinanzOnline - ACTIVATED */}
          <div 
            onClick={() => setActiveModal('FINANZONLINE')}
            className="bg-slate-900/50 backdrop-blur-sm border border-white/5 p-6 rounded-2xl relative group hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer hover:shadow-2xl hover:shadow-blue-900/20"
          >
              <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-white font-bold text-xs text-center border border-slate-700 shadow-lg">Finanz<br/>Online</div>
                  <div className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-bold px-2 py-1 rounded-full flex items-center">
                      PREVIEW
                  </div>
              </div>
              <h4 className="font-bold text-white text-lg group-hover:text-blue-400 transition-colors">Fristen-Wächter</h4>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                  KI prüft Bescheide auf Fristen und gleicht diese mit dem Kalender ab.
              </p>
              <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-center">
                  <span className="text-xs text-slate-500 font-mono flex items-center"><ShieldCheck className="w-3 h-3 mr-1" /> DataBox Scan</span>
                  <span className="text-blue-400 text-xs font-bold flex items-center opacity-0 group-hover:opacity-100 transition-opacity transform translate-x-[-10px] group-hover:translate-x-0 duration-300">
                      Demo <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
              </div>
          </div>
      </div>
    </div>
  );
};

export default IntegrationsHub;
